import type { KioskVisitorConfig } from './visitors';
/** Geofenced clock-in and shared-tablet kiosks (backlog #26). Pure helpers shared by API, web and tests. */

export type GeofenceMode = 'off' | 'warn' | 'enforce';
/** inside / outside the fence, unknown (no or poor reading), kiosk (fixed device), not_checked (fence off). */
export type GeoStatus = 'inside' | 'outside' | 'unknown' | 'kiosk' | 'not_checked';

export interface Geofence {
  location_id: string;
  location_name?: string;
  mode: GeofenceMode;
  lat: number | null;
  lng: number | null;
  /** Effective values (the building's own ?? the workspace default): what every check uses. */
  radius_m: number;
  max_accuracy_m: number;
  /** t467u: the building's own values; null = inherits the workspace default. Absent from older APIs. */
  radius_override_m?: number | null;
  max_accuracy_override_m?: number | null;
  default_radius_m?: number;
  default_max_accuracy_m?: number;
  /** The building's own address coordinates, if it has them (for "Use the building's address"). */
  building_lat?: number | null;
  building_lng?: number | null;
  updated_at?: string | null;
  /** The building's address (trimmed; null when it has none). */
  address?: string | null;
  /** Where the pin came from: the building's address (follows address changes) or placed by an admin. */
  location_source?: GeofencePinSource;
  /** The address last geocoded (or tried) and whether that failed. */
  geocoded_address?: string | null;
  geocode_failed?: boolean;
}
export type GeofencePinSource = 'address' | 'manual';

export const PIN_FROM_ADDRESS_NOTE = 'Using the building’s address';
export const RESET_PIN_TO_ADDRESS_LABEL = 'Reset to address';
/**
 * The muted line under a building's clock-in pin: "Using the building's address", or why there is no pin yet.
 * `edited` (the form's coordinates differ from what is saved): no note, the admin is placing the pin themselves.
 */
export function geofencePinNote(g: Pick<Geofence, 'lat' | 'lng' | 'address' | 'location_source' | 'geocoded_address' | 'geocode_failed'>, edited = false): string | null {
  if (edited || g.location_source === 'manual') return null;
  const pinned = g.lat != null && g.lng != null;
  if (!g.address) return pinned ? null : 'Add the building’s address to place the pin automatically, or enter the coordinates.';
  const triedThis = g.geocoded_address === g.address;
  if (g.geocode_failed && triedThis) {
    return pinned ? 'We couldn’t find the new address on the map, so the pin is still at the old one. Check the address, or move the pin.'
      : 'We couldn’t find the building’s address on the map. Check the address, or enter the coordinates.';
  }
  return pinned ? PIN_FROM_ADDRESS_NOTE : null;
}
/** Reset to address: offered once an admin has placed the pin themselves and the building has an address. */
export const canResetPinToAddress = (g: Pick<Geofence, 'address' | 'location_source'>) => g.location_source === 'manual' && Boolean(g.address);

/** t467u: workspace-wide geofence defaults; each building inherits them unless it has its own value. */
export interface GeofenceDefaults { radius_m: number; max_accuracy_m: number }
export const GEOFENCE_DEFAULT_RADIUS_M = 150;
export const GEOFENCE_DEFAULT_MAX_ACCURACY_M = 150;
export const GEOFENCE_RADIUS_HINT = 'How far from the pin someone can be and still count as on site.';
export const GEOFENCE_ACCURACY_HINT = 'Phones say how sure they are of their position. Readings less precise than this count as unknown, not in or out.';
/** What evaluateGeofence (and offline punches) actually do with an unknown reading. */
export const GEOFENCE_UNKNOWN_NOTE = 'Unknown readings: with Warn, people can clock in and the entry shows Location unknown. With Enforce, clock-in is refused (clock-out never is). Offline clock-ins are let through and flagged for review.';
export const GEOFENCE_LAT_PLACEHOLDER = 'e.g. 53.4808';
export const GEOFENCE_LNG_PLACEHOLDER = 'e.g. -2.2426';
/** "Uses default (150 m)". */
export const inheritPlaceholder = (m: number) => `Uses default (${m} m)`;
/** The effective radius / accuracy for a building: its own value, else the workspace default. */
export const effectiveFence = (own: { radius_m?: number | null; max_accuracy_m?: number | null }, d: GeofenceDefaults): GeofenceDefaults =>
  ({ radius_m: own.radius_m ?? d.radius_m, max_accuracy_m: own.max_accuracy_m ?? d.max_accuracy_m });

export interface GeoReading { lat: number; lng: number; accuracy_m: number }

export interface GeofenceResult {
  geo: GeoStatus;
  distance_m: number | null;
  /** False only when the fence is enforced and the reading is outside or unusable (clock-in only). */
  allowed: boolean;
  message: string | null;
}

const R = 6371008.8;
const rad = (d: number) => (d * Math.PI) / 180;

/** Great-circle distance in metres. */
export function haversineMetres(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Evaluates a reading against a building's fence. The reading's accuracy circle is given the benefit of the doubt:
 * someone is "inside" when their position is within radius + accuracy. A reading less precise than max_accuracy_m
 * is "unknown". Clock-out (`direction: 'out'`) is never blocked: people must always be able to stop the clock.
 */
export function evaluateGeofence(fence: Geofence | null, reading: GeoReading | null | undefined, direction: 'in' | 'out' = 'in'): GeofenceResult {
  if (!fence || fence.mode === 'off' || fence.lat == null || fence.lng == null) return { geo: 'not_checked', distance_m: null, allowed: true, message: null };
  const enforce = fence.mode === 'enforce' && direction === 'in';
  if (!reading || !Number.isFinite(reading.lat) || !Number.isFinite(reading.lng)) {
    return { geo: 'unknown', distance_m: null, allowed: !enforce,
      message: enforce ? 'Location is needed to clock in at this building. Allow location access and try again, or use the kiosk.' : 'We couldn\'t check your location. Your manager will see this.' };
  }
  const distance = Math.round(haversineMetres(reading, { lat: fence.lat, lng: fence.lng }));
  if (!(reading.accuracy_m >= 0) || reading.accuracy_m > fence.max_accuracy_m) {
    return { geo: 'unknown', distance_m: distance, allowed: !enforce,
      message: enforce ? `Your location isn't accurate enough (±${Math.round(reading.accuracy_m)} m). Move near a window or use the kiosk.` : 'Your location wasn\'t accurate enough to check. Your manager will see this.' };
  }
  if (distance <= fence.radius_m + reading.accuracy_m) return { geo: 'inside', distance_m: distance, allowed: true, message: null };
  return { geo: 'outside', distance_m: distance, allowed: !enforce,
    message: enforce ? `You're ${formatDistance(distance)} from the building. Clock in when you're on site, or use the kiosk.` : `You're ${formatDistance(distance)} from the building. Your manager will see this.` };
}

export function formatDistance(m: number): string {
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(m < 10000 ? 1 : 0)} km`;
}

export const GEO_LABELS: Record<GeoStatus, string> = {
  inside: 'On site', outside: 'Off site', unknown: 'Location unknown', kiosk: 'Kiosk', not_checked: 'Not checked',
};

/** PIN rules: 4-6 digits, not all the same digit, not a straight run (1234, 9876). Returns an error or null. */
export function pinProblem(pin: string): string | null {
  if (!/^\d{4,6}$/.test(pin)) return 'Use 4 to 6 digits';
  if (/^(\d)\1+$/.test(pin)) return 'Don\'t use the same digit throughout';
  const d = [...pin].map(Number);
  const step = d[1] - d[0];
  if (Math.abs(step) === 1 && d.every((x, i) => i === 0 || x - d[i - 1] === step)) return 'Don\'t use a run of digits like 1234';
  return null;
}

export const KIOSK_QR_PREFIX = 'tokiosk1:';
export const KIOSK_PIN_MAX_ATTEMPTS = 5;
export const KIOSK_PIN_LOCK_MINUTES = 15;

export interface Kiosk {
  id: string;
  location_id: string;
  location_name: string;
  name: string;
  token_hint: string;
  photo_mode: 'off' | 'on';
  allow_qr: boolean;
  created_at: string;
  created_by_name: string | null;
  last_seen_at: string | null;
  revoked_at: string | null;
}

export interface KioskRegistered { kiosk: Kiosk; token: string; url: string }

export type KioskPersonState = 'out' | 'in' | 'break';
export interface KioskPerson {
  user_id: string;
  display_name: string;
  initials: string;
  state: KioskPersonState;
  has_pin: boolean;
  shift: { starts_at: string; ends_at: string; position_name: string | null } | null;
}

export interface KioskSession {
  kiosk: { id: string; name: string; location_name: string; timezone: string; photo_mode: 'off' | 'on'; allow_qr: boolean };
  people: KioskPerson[];
  /** t369u: the Time clock module is switched off for the workspace (the kiosk only signs visitors in). */
  clock_off?: boolean;
  /** t335u item 5: visitor sign-in on this kiosk (null when the building hasn't switched it on). */
  visitors?: KioskVisitorConfig | null;
}

export type KioskAction = 'clock_in' | 'clock_out' | 'break_start' | 'break_end';
export interface KioskActionResult {
  user_id: string;
  display_name: string;
  action: KioskAction;
  at: string;
  state: KioskPersonState;
  worked_minutes: number | null;
  message: string;
}

export interface KioskEvent {
  id: number;
  kiosk_id: string;
  kiosk_name: string;
  user_id: string | null;
  display_name: string | null;
  action: string;
  method: 'pin' | 'qr' | null;
  created_at: string;
}

export interface KioskCredentialStatus { has_pin: boolean; pin_set_at: string | null; has_qr: boolean; qr_set_at: string | null; locked_until: string | null }

/** Initials for kiosk name tiles. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
}

/** t182u: an optional id from a form control: '' (an empty select) or whitespace is "not chosen", never sent. */
export function optionalId(v: string | null | undefined): string | undefined {
  const s = typeof v === 'string' ? v.trim() : '';
  return s ? s : undefined;
}

/** A building someone can clock in at without a shift (from GET time/current). */
export interface ClockLocation { id: string; name: string; lat: number | null; lng: number | null }

/** How close a position must be for "nearest building" to win over the home building. */
export const NEAREST_BUILDING_MAX_M = 2000;

/**
 * t182u: the building an unscheduled clock-in defaults to. The only candidate when there is one; otherwise the nearest
 * building with a position, when a usable reading puts you within NEAREST_BUILDING_MAX_M of it; otherwise your home
 * (primary) building; otherwise the first in the list. Null only when there are no candidates.
 */
export function pickClockLocation(candidates: ClockLocation[], opts: { home?: string | null; position?: GeoReading | null } = {}): string | null {
  if (!candidates.length) return null;
  if (candidates.length === 1) return candidates[0].id;
  const pos = opts.position;
  if (pos && Number.isFinite(pos.lat) && Number.isFinite(pos.lng) && pos.accuracy_m <= NEAREST_BUILDING_MAX_M) {
    let best: { id: string; d: number } | null = null;
    for (const c of candidates) {
      if (c.lat == null || c.lng == null) continue;
      const d = haversineMetres(pos, { lat: c.lat, lng: c.lng });
      if (!best || d < best.d) best = { id: c.id, d };
    }
    if (best && best.d <= NEAREST_BUILDING_MAX_M) return best.id;
  }
  if (opts.home && candidates.some(c => c.id === opts.home)) return opts.home;
  return candidates[0].id;
}
